import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { SingleSelectCards } from '../../components/quiz';
import { Button } from '../../components/ui';
import { track, page, EVENTS } from '../../utils/analytics';
import { buildStressSignupUrl } from '../../utils/signupUrl';
import { useApp } from '../../contexts/AppContext';
import type { SelectableCardOption } from '../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'intro' | 'quiz' | 'loading' | 'results';
type ArchetypeKey = 'operator' | 'builder' | 'captive' | 'reactor';

interface AnswerEntry {
  optionId: string;
  archetype: ArchetypeKey;
}

interface QuizOption extends SelectableCardOption {
  archetype: ArchetypeKey;
}

interface QuizQuestion {
  key: string;
  text: string;
  subtext?: string;
  options: QuizOption[];
}

// ── Question definitions ──────────────────────────────────────────────────────

const QUESTIONS: QuizQuestion[] = [
  {
    key: 'business_type',
    text: 'Which best describes your business right now?',
    subtext: 'This helps us personalize your results.',
    options: [
      { id: 'SOLO', title: "I'm a solo freelancer or independent consultant", archetype: 'builder' },
      { id: 'SMALL_TEAM', title: "I run a small business with a team", archetype: 'operator' },
      { id: 'SIDE_BIZ', title: "I have a side business alongside a full-time job", archetype: 'operator' },
      { id: 'MULTI_REVENUE', title: "I manage multiple income streams or clients", archetype: 'operator' },
    ],
  },
  {
    key: 'relief_vision',
    text: 'If your business finances ran on complete autopilot - what would that free up?',
    options: [
      { id: 'TIME_TO_GROW', title: 'Time to focus on clients and growth - not moving money around', archetype: 'operator' },
      { id: 'MENTAL_QUIET', title: 'Mental quiet - no more financial noise running in the background', archetype: 'reactor' },
      { id: 'TAX_CONFIDENCE', title: 'Confidence that taxes and cash flow are always sorted', archetype: 'builder' },
      { id: 'REAL_PROFIT', title: 'Clarity on what I actually keep after expenses and taxes', archetype: 'captive' },
    ],
  },
  {
    key: 'financial_admin_time',
    text: 'How much time do you spend each month on financial admin?',
    subtext: 'Moving money, checking balances, categorizing transactions, chasing invoices',
    options: [
      { id: 'UNDER_HOUR', title: 'Under an hour - I have some automation in place', archetype: 'builder' },
      { id: 'FEW_HOURS', title: '2-4 hours - mostly manual but manageable', archetype: 'operator' },
      { id: 'HALF_DAY', title: '5-10 hours - it feels like a second job', archetype: 'operator' },
      { id: 'LOST_COUNT', title: "I've stopped tracking - it bleeds into everything", archetype: 'reactor' },
    ],
  },
  {
    key: 'biz_personal_separation',
    text: 'How separated are your business and personal finances?',
    options: [
      { id: 'CLEAN_SEP', title: 'Completely separated - different accounts, clean records', archetype: 'builder' },
      { id: 'MOSTLY_SEP', title: "Mostly separated, but it gets messy under pressure", archetype: 'operator' },
      { id: 'SOME_OVERLAP', title: 'They overlap more than they should', archetype: 'operator' },
      { id: 'ALL_MIXED', title: "One big mix - I sort it out at tax time and hope for the best", archetype: 'reactor' },
    ],
  },
  {
    key: 'tax_setup',
    text: 'How do you handle setting money aside for taxes?',
    options: [
      { id: 'AUTO_TAX', title: 'Automatically - a percentage moves to a tax account with every deposit', archetype: 'builder' },
      { id: 'PERIODIC_MOVE', title: 'I transfer money over when I remember to', archetype: 'operator' },
      { id: 'MENTAL_NOTE', title: "I keep a rough mental estimate - nothing is actually set aside", archetype: 'reactor' },
      { id: 'YEAR_END', title: "I deal with it at year-end and hope there's enough left", archetype: 'captive' },
    ],
  },
  {
    key: 'client_payment_flow',
    text: 'When a client payment hits your account, what actually happens next?',
    options: [
      { id: 'AUTO_SPLIT', title: 'It splits automatically into tax, expenses, and profit', archetype: 'builder' },
      { id: 'MANUAL_SORT', title: "I manually move it around - I get to it, but it takes time", archetype: 'operator' },
      { id: 'ONE_POOL', title: 'It goes into one account and I pull from it as needed', archetype: 'captive' },
      { id: 'MIXED_IN', title: "It mixes with everything else and I lose track of where it went", archetype: 'reactor' },
    ],
  },
  {
    key: 'biggest_frustration',
    text: "What's the hardest part of managing your business finances right now?",
    options: [
      { id: 'NO_SYSTEM', title: "I don't have a real system - I'm figuring it out as I go", archetype: 'reactor' },
      { id: 'KNOW_NOT_DO', title: "I know what I should be doing - I just can't execute consistently", archetype: 'reactor' },
      { id: 'TOO_SCATTERED', title: "Too many accounts and tools pulling in different directions", archetype: 'operator' },
      { id: 'ALWAYS_BEHIND', title: "I'm always reacting to problems instead of staying ahead of them", archetype: 'captive' },
    ],
  },
  {
    key: 'sorted_vision',
    text: "What would 'financially sorted' look like for your business?",
    options: [
      { id: 'TAX_CLARITY', title: 'Knowing exactly what I owe in taxes at any point in the year', archetype: 'builder' },
      { id: 'PROFIT_VISIBILITY', title: 'A clear picture of real profit - not just revenue hitting an account', archetype: 'captive' },
      { id: 'AUTO_FLOW', title: 'Money automatically in the right place without me touching it', archetype: 'operator' },
      { id: 'LESS_ADMIN', title: 'Spending my energy on work - not financial admin', archetype: 'reactor' },
    ],
  },
];

const TOTAL_QUESTIONS = QUESTIONS.length;

// ── Archetype definitions ─────────────────────────────────────────────────────

interface Archetype {
  name: string;
  tagline: string;
  descHeading: string;
  p1: string;
  p2: string;
  reliefStat: string;
  reliefStatDesc: string;
  ctaSub: string;
  reliefScore: number;
}

const ARCHETYPES: Record<ArchetypeKey, Archetype> = {
  operator: {
    name: 'The Exhausted Operator',
    tagline: "You're running a real business - but your finances are running you, not the other way around.",
    descHeading: 'The relief is in removing the manual work',
    p1: "You've built something real. But the financial side of it demands constant attention - moving money, checking balances, calculating what to set aside. Every week it takes time and headspace you should be spending on the work that actually earns.",
    p2: "The fix isn't better discipline. It's a system that moves money the way you'd want it moved, automatically, every time revenue comes in. Sequence was built exactly for this. You set the rules once - and the juggling act ends.",
    reliefStat: '6 hrs',
    reliefStatDesc: 'of financial admin reclaimed per month on average',
    ctaSub: "Based on your answers, Sequence will automate the transfers, allocations, and account management that's currently eating your time - so the right money is always in the right place before you've had to think about it.",
    reliefScore: 84,
  },
  builder: {
    name: 'The Ambitious Builder',
    tagline: "You have a real business with real momentum - but your financial setup hasn't kept pace with your growth.",
    descHeading: 'The relief is in closing the gap between what you know and what runs automatically',
    p1: "You understand how your finances should work. You have some separation, some system, some structure. The problem is that it still requires you. When you're busy growing - which is most of the time - the cracks show.",
    p2: "Sequence takes the parts of your setup that still depend on you and makes them run on their own. Profit First flows, automated tax set-asides, clean business/personal separation - all in the background while you focus on what moves the needle.",
    reliefStat: '8 hrs',
    reliefStatDesc: 'saved per month by business owners at your stage',
    ctaSub: "Based on your answers, Sequence will automate your Profit First allocation, tax buckets, and account separation - so your system holds up even in your busiest months.",
    reliefScore: 76,
  },
  captive: {
    name: 'The Cash Flow Captive',
    tagline: "Revenue comes in - but it never feels like enough. Because there's no system catching it before it disappears.",
    descHeading: 'The relief is in seeing where your money actually goes',
    p1: "You're not spending recklessly. Money just lands in one place and gets pulled in every direction before you've decided where it should go. By the time you want to pay yourself, set aside taxes, or save - there's nothing clearly available.",
    p2: "Sequence flips this around. Money arrives, and rules immediately allocate it - taxes, owner's pay, operating expenses - before anything else can claim it. You stop reacting and start running your business like it's profitable, because you'll finally be able to see that it is.",
    reliefStat: '$600+',
    reliefStatDesc: 'redirected toward actual goals per month on average',
    ctaSub: "Based on your answers, Sequence will build an automatic allocation flow so every deposit is sorted before you touch it - taxes set aside, profit visible, cash flow finally making sense.",
    reliefScore: 80,
  },
  reactor: {
    name: 'The Reactive Owner',
    tagline: "You're running your business day to day - but your finances feel like a problem you'll deal with tomorrow.",
    descHeading: 'The relief is in building a system you can actually trust',
    p1: "The anxiety isn't about math. It's about not having a structure you trust - so every financial decision carries more weight than it should. You check less, automate less, and carry the low-grade stress of not knowing if things are under control.",
    p2: "You don't need more discipline. You need a system that works without requiring it. Sequence gives you that - rules that move money automatically, tax buckets that fill themselves, and a clear picture of where everything stands. Set it up once and stop carrying it.",
    reliefStat: '73%',
    reliefStatDesc: 'of users report meaningful relief within the first month',
    ctaSub: "Based on your answers, Sequence will build you a system you can fully trust - so the background noise stops and your business finances finally run themselves.",
    reliefScore: 88,
  },
};

// ── Loading steps ─────────────────────────────────────────────────────────────

const LOADING_ITEMS = [
  'Mapping your business financial setup...',
  'Identifying your biggest relief opportunities...',
  'Building your personalized automation plan...',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getWinningArchetype(answers: Record<string, AnswerEntry>): ArchetypeKey {
  const tally: Record<ArchetypeKey, number> = { operator: 0, builder: 0, captive: 0, reactor: 0 };
  Object.values(answers).forEach(({ archetype }) => {
    tally[archetype]++;
  });
  let winner: ArchetypeKey = 'reactor';
  let max = -1;
  (Object.keys(tally) as ArchetypeKey[]).forEach((key) => {
    if (tally[key] > max) { max = tally[key]; winner = key; }
  });
  return winner;
}

function getDevice(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ── Intro illustration ────────────────────────────────────────────────────────

function IntroIllustration() {
  return (
    <svg width="148" height="128" viewBox="0 0 148 128" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Background glow */}
      <circle cx="74" cy="68" r="56" fill="#ede9fe" />
      {/* Central circle */}
      <circle cx="74" cy="64" r="32" fill="white" stroke="#7c3aed" strokeWidth="2" />
      {/* Upward arrow - relief / growth */}
      <path d="M74 82 L74 48" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M64 58 L74 48 L84 58" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Small dots representing money flows becoming ordered */}
      <circle cx="56" cy="72" r="4" fill="#c4b5fd" />
      <circle cx="92" cy="72" r="4" fill="#c4b5fd" />
      <circle cx="64" cy="80" r="3" fill="#ddd6fe" />
      <circle cx="84" cy="80" r="3" fill="#ddd6fe" />
      {/* Arrows from dots to center - converging */}
      <path d="M59 70 L68 66" stroke="#a78bfa" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M89 70 L80 66" stroke="#a78bfa" strokeWidth="1.5" strokeLinecap="round" />
      {/* Shield top-right */}
      <path d="M116 18 L132 26 L132 46 Q132 57 116 62 Q100 57 100 46 L100 26 Z" fill="#ede9fe" />
      <path d="M116 22 L129 29 L129 46 Q129 55 116 59 Q103 55 103 46 L103 29 Z" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinejoin="round" />
      <path d="M109 45 L114 50 L124 38" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SequenceQuiz() {
  const { setFlow } = useApp();

  const [phase, setPhase] = useState<Phase>('intro');
  const [currentQ, setCurrentQ] = useState(1);
  const [answers, setAnswers] = useState<Record<string, AnswerEntry>>({});
  const [currentSelection, setCurrentSelection] = useState<string | null>(null);
  const [autoAdvancing, setAutoAdvancing] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [archetypeKey, setArchetypeKey] = useState<ArchetypeKey>('reactor');
  const [scoreDisplay, setScoreDisplay] = useState(0);

  const utmRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'utm_landing_page'];
    const utms: Record<string, string> = {};
    utmKeys.forEach((key) => {
      const val = params.get(key);
      if (val) utms[key] = val;
    });
    if (!utms.utm_landing_page) utms.utm_landing_page = 'stress';
    utmRef.current = utms;

    setFlow('stress');
    page('Stress Quiz');
    track(EVENTS.STRESS_QUIZ_SHOWN, { flow: 'stress', device: getDevice(), ...utms });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [currentQ, phase]);

  useEffect(() => {
    if (phase !== 'quiz') return;
    const key = `q${currentQ}`;
    setCurrentSelection(answers[key]?.optionId ?? null);
  }, [currentQ, phase]);

  // Loading animation
  useEffect(() => {
    if (phase !== 'loading') return;
    setLoadingStep(0);
    const timers = [
      setTimeout(() => setLoadingStep(1), 600),
      setTimeout(() => setLoadingStep(2), 1400),
      setTimeout(() => setLoadingStep(3), 2200),
      setTimeout(() => {
        const winner = getWinningArchetype(answers);
        setArchetypeKey(winner);
        setPhase('results');
      }, 3400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  // Animate relief score counter on results
  useEffect(() => {
    if (phase !== 'results') return;
    const target = ARCHETYPES[archetypeKey].reliefScore;
    setScoreDisplay(0);
    let counter = 0;
    const interval = setInterval(() => {
      counter += 2;
      if (counter >= target) { counter = target; clearInterval(interval); }
      setScoreDisplay(counter);
    }, 28);
    return () => clearInterval(interval);
  }, [phase, archetypeKey]);

  function handleOptionSelect(optionId: string) {
    if (autoAdvancing) return;

    const question = QUESTIONS[currentQ - 1];
    const option = question.options.find((o) => o.id === optionId);
    if (!option) return;

    setCurrentSelection(optionId);
    setAutoAdvancing(true);

    const key = `q${currentQ}`;
    const newAnswers: Record<string, AnswerEntry> = {
      ...answers,
      [key]: { optionId, archetype: option.archetype },
    };
    setAnswers(newAnswers);

    if (currentQ === 1) {
      track(EVENTS.STRESS_QUIZ_STARTED, {
        flow: 'stress',
        segment: optionId,
        device: getDevice(),
        ...utmRef.current,
      });
    }

    track(EVENTS.STRESS_QUESTION_ANSWERED, {
      flow: 'stress',
      questionNumber: currentQ,
      questionKey: question.key,
      answerId: optionId,
      archetypeSignal: option.archetype,
      device: getDevice(),
      ...utmRef.current,
    });

    setTimeout(() => {
      setAutoAdvancing(false);
      if (currentQ === TOTAL_QUESTIONS) {
        const winner = getWinningArchetype(newAnswers);
        track(EVENTS.STRESS_QUIZ_COMPLETED, {
          flow: 'stress',
          archetype: winner,
          archetypeName: ARCHETYPES[winner].name,
          reliefScore: ARCHETYPES[winner].reliefScore,
          answers: Object.fromEntries(
            Object.entries(newAnswers).map(([k, v]) => [k, v.optionId])
          ),
          device: getDevice(),
          ...utmRef.current,
        });
        setPhase('loading');
      } else {
        setCurrentQ((prev) => prev + 1);
        setCurrentSelection(null);
      }
    }, 320);
  }

  function handleBack() {
    if (phase === 'quiz') {
      if (currentQ === 1) {
        setPhase('intro');
        return;
      }
      setCurrentQ((prev) => prev - 1);
      setCurrentSelection(null);
    }
  }

  const progressPct =
    phase === 'loading' || phase === 'results' ? 100
    : phase === 'intro' ? 0
    : Math.round(((currentQ - 1) / TOTAL_QUESTIONS) * 100);

  function ProgressHeader({ showBack = false }: { showBack?: boolean }) {
    return (
      <div className="rental-layout__progress">
        <div className="rental-progress">
          <div className="rental-progress__bar">
            <div className="rental-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="rental-progress__meta">
            <span className="rental-progress__pct">{progressPct}%</span>
          </div>
        </div>
        {showBack && (
          <button className="rental-back-btn" onClick={handleBack} aria-label="Go back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </button>
        )}
      </div>
    );
  }

  // ── Render: intro ─────────────────────────────────────────────────────────────

  if (phase === 'intro') {
    return (
      <>
        <Helmet>
          <title>What's Your Business Financial Relief Potential? | Sequence</title>
          <meta name="description" content="8 questions built for small business owners. Find out exactly how much financial relief your business could get - and what's standing in the way." />
        </Helmet>
        <div className="stress-intro">
          <div className="stress-intro__container">
            <div className="stress-intro__content">
              <IntroIllustration />
              <h1 className="stress-intro__headline">
                What's your business financial relief potential?
              </h1>
              <p className="stress-intro__subheadline">
                8 questions built for small business owners. Find out exactly where relief is waiting - and what's standing in the way.
              </p>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => {
                  setPhase('quiz');
                  setCurrentQ(1);
                  setCurrentSelection(null);
                }}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                Find out in 2 minutes
              </Button>
              <p className="stress-intro__trust">Free. No account needed.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: loading ───────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <>
        <Helmet>
          <title>Building Your Automation Plan... | Sequence</title>
        </Helmet>
        <div className="stress-loading">
          <div className="stress-loading__container">
            <div className="stress-loading__spinner" aria-hidden="true" />
            <h1 className="stress-loading__title">Building your personalized plan...</h1>
            <div className="stress-loading__checklist">
              {LOADING_ITEMS.map((item, i) => {
                const done = i < loadingStep - 1;
                const active = i === loadingStep - 1;
                return (
                  <div
                    key={i}
                    className={`stress-loading__item${
                      done ? ' stress-loading__item--done'
                      : active ? ' stress-loading__item--active'
                      : i < loadingStep ? ' stress-loading__item--visible'
                      : ''
                    }`}
                  >
                    <span className="stress-loading__item-dot" />
                    <span className="stress-loading__item-text">{item}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: results ───────────────────────────────────────────────────────────

  if (phase === 'results') {
    const archetype = ARCHETYPES[archetypeKey];
    const signupUrl = buildStressSignupUrl({ archetype: archetypeKey, utms: utmRef.current });

    return (
      <>
        <Helmet>
          <title>Your Business Financial Relief Profile | Sequence</title>
        </Helmet>
        <div className="stress-results">

          {/* Hero */}
          <div className="stress-results__hero">
            <div className="stress-results__hero-inner">
              <div className="stress-results__hero-left">
                <div className="stress-results__archetype-badge">Your financial relief profile</div>
                <h1 className="stress-results__archetype-name">{archetype.name}</h1>
                <p className="stress-results__tagline">{archetype.tagline}</p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    track(EVENTS.SEQUENCE_SIGNUP_CLICKED, {
                      flow: 'stress',
                      archetype: archetypeKey,
                      reliefScore: archetype.reliefScore,
                      ctaLocation: 'hero',
                      ...utmRef.current,
                    });
                    window.location.href = signupUrl;
                  }}
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  }
                  iconPosition="right"
                >
                  See my automation plan
                </Button>
              </div>
              <div className="stress-results__score-card">
                <p className="stress-results__score-label">Relief potential</p>
                <div className="stress-results__score-number">{scoreDisplay}</div>
                <p className="stress-results__score-sub">out of 100</p>
                <div className="stress-results__score-bar-wrap">
                  <div
                    className="stress-results__score-bar-fill"
                    style={{ width: phase === 'results' ? `${archetype.reliefScore}%` : '0%' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Description + Stats */}
          <div className="stress-results__description">
            <div className="stress-results__description-inner">
              <div className="stress-results__desc-main">
                <h2 className="stress-results__desc-heading">{archetype.descHeading}</h2>
                <p className="stress-results__desc-p">{archetype.p1}</p>
                <p className="stress-results__desc-p">{archetype.p2}</p>
              </div>
              <div className="stress-results__stats">
                <div className="stress-results__stat-card">
                  <div className="stress-results__stat-number">{archetype.reliefStat}</div>
                  <div className="stress-results__stat-desc">{archetype.reliefStatDesc}</div>
                </div>
                <div className="stress-results__stat-card">
                  <div className="stress-results__stat-number">73%</div>
                  <div className="stress-results__stat-desc">of users report meaningful relief within the first month</div>
                </div>
                <div className="stress-results__stat-card">
                  <div className="stress-results__stat-number">30 days</div>
                  <div className="stress-results__stat-desc">money-back guarantee - zero risk to try it</div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="stress-results__testimonials">
            <div className="stress-results__testimonials-inner">
              <p className="stress-results__testimonials-label">Real business owners, real relief</p>
              <h2 className="stress-results__testimonials-heading">You're not the only one who's felt this way</h2>
              <div className="stress-results__testimonials-grid">
                <div className="stress-results__testimonial-card">
                  <p className="stress-results__testimonial-quote">"I was about to have an aneurysm with my manual tracking spreadsheets. Whatever you guys are doing, you're literally doing God's work."</p>
                  <div className="stress-results__testimonial-author">Dylan E.</div>
                  <div className="stress-results__testimonial-handle">Sequence user</div>
                </div>
                <div className="stress-results__testimonial-card">
                  <p className="stress-results__testimonial-quote">"I absolutely agree with Sequence being a game changer. I've always had a plan for my finances, but typically haven't had the time to execute it consistently. Sequence has changed that."</p>
                  <div className="stress-results__testimonial-author">Sequence user</div>
                  <div className="stress-results__testimonial-handle">Business owner</div>
                </div>
                <div className="stress-results__testimonial-card">
                  <p className="stress-results__testimonial-quote">"I feel like you're lifting a giant weight right off my shoulders. You have no idea how helpful this is."</p>
                  <div className="stress-results__testimonial-author">Reef C.</div>
                  <div className="stress-results__testimonial-handle">Sequence user</div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="stress-results__cta">
            <div className="stress-results__cta-inner">
              <p className="stress-results__cta-eyebrow">Your automation plan is ready</p>
              <h2 className="stress-results__cta-headline">Stop managing your money. Let Sequence do it.</h2>
              <p className="stress-results__cta-sub">{archetype.ctaSub}</p>
              <Button
                variant="primary"
                size="lg"
                className="stress-results__cta-btn"
                onClick={() => {
                  track(EVENTS.SEQUENCE_SIGNUP_CLICKED, {
                    flow: 'stress',
                    archetype: archetypeKey,
                    reliefScore: archetype.reliefScore,
                    ctaLocation: 'bottom',
                    ...utmRef.current,
                  });
                  window.location.href = signupUrl;
                }}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                Start my free trial
              </Button>
              <p className="stress-results__cta-guarantee">30-day money-back guarantee - bank-level security via Plaid</p>
            </div>
          </div>

        </div>
      </>
    );
  }

  // ── Render: quiz question ─────────────────────────────────────────────────────

  const question = QUESTIONS[currentQ - 1];
  const cardOptions: SelectableCardOption[] = question.options.map((o) => ({
    id: o.id,
    title: o.title,
  }));

  return (
    <>
      <Helmet>
        <title>What's Your Business Financial Relief Potential? | Sequence</title>
        <meta name="description" content="8 questions for small business owners. Find out where financial relief is waiting." />
      </Helmet>
      <div className="rental-layout">
        <ProgressHeader showBack />
        <main className="rental-layout__content">
          <div className="rental-layout__container">
            <div className="quiz-step">
              <div className="quiz-step__header">
                <p className="stress-quiz__step-label">Question {currentQ} of {TOTAL_QUESTIONS}</p>
                <h2 className="quiz-step__question">{question.text}</h2>
                {question.subtext && (
                  <p className="quiz-step__helper">{question.subtext}</p>
                )}
              </div>
              <div className="quiz-step__body">
                <SingleSelectCards
                  options={cardOptions}
                  value={currentSelection}
                  onChange={handleOptionSelect}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
